import { add, endOfWeek, getYear, isAfter, isBefore, startOfWeek } from 'date-fns';

export class ScheduleUtil {
  public static firstDayOfYear(year: number): Date {
    if (year < 1 || year > 9999) throw new Error('Invalid year');

    return new Date(year, 0, 1);
  }

  public static lastDayOfYear(year: number): Date {
    if (year < 1 || year > 9999) throw new Error('Invalid year');

    return new Date(year, 11, 31);
  }

  public static get thisYearFirstDay(): Date {
    return ScheduleUtil.firstDayOfYear(getYear(new Date()));
  }

  public static get thisYearLastDay(): Date {
    return ScheduleUtil.lastDayOfYear(getYear(new Date()));
  }
  public static isBeforeThisWeek(date: Date): boolean {
    if (!date || !(date instanceof Date)) return false;

    const thisMonday = startOfWeek(new Date(), { weekStartsOn: 1 });

    return isBefore(date, thisMonday);
  }

  public static isThisWeek(date: Date): boolean {
    if (!date || !(date instanceof Date)) return false;

    const thisMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
    const nextMonday = add(thisMonday, { days: 6 });

    return isAfter(date, thisMonday) && isBefore(date, nextMonday);
  }

  public static isAfterThisWeek(date: Date): boolean {
    if (!date || !(date instanceof Date)) return false;

    const thisSunday = endOfWeek(new Date(), { weekStartsOn: 1 });
    return isAfter(date, thisSunday);
  }

  public static isInTheFuture(date: Date): boolean {
    if (!date || !(date instanceof Date)) return false;

    const now = new Date();
    return isAfter(date, now);
  }

  public static isInThePast(date: Date): boolean {
    if (!date || !(date instanceof Date)) return false;

    const now = new Date();
    return isBefore(date, now);
  }

  /**
   * Converts a Date object to ISO string format suitable for HTTP requests.
   *
   * @param date - The Date object to be converted to ISO string format
   * @returns The ISO string representation of the date (YYYY-MM-DDTHH:mm:ss.sssZ format)
   */
  public static toHttpRequestFormat(date: Date): string {
    if (!date || !(date instanceof Date)) throw new Error('Invalid date');

    return date instanceof Date ? date.toISOString() : new Date(date).toISOString();
  }

  /**
   * Calculates and formats the time delay between a given date and today in French.
   *
   * Returns a human-readable string representing the duration between the provided date
   * and the current date. The format adapts based on the duration (days, weeks, months, or years).
   * Returns an empty string if the date direction doesn't match the inPast parameter.
   *
   * @param date - The date to calculate the delay from
   * @param inPast - Whether to calculate delay for a past date (true) or future date (false).
   *                 Defaults to true. When true, returns "Il y a X" format. When false, returns "Dans X" format.
   * @returns A formatted French string describing the time delay (e.g., "Il y a 3 jours", "Dans 2 semaines"),
   *          or an empty string if the date direction doesn't match the inPast parameter
   */
  public static delaySinceToday(date: Date): string {
    const inPast = ScheduleUtil.isInThePast(date);

    const duration = inPast
      ? Math.abs(date.getTime() - new Date().getTime())
      : Math.abs(new Date().getTime() - date.getTime());

    let announcement = inPast ? 'Il y a ' : 'Dans ';

    const nbrDays = Math.floor(duration / (1000 * 60 * 60 * 24));
    const nbrWeeks = Math.floor(duration / (1000 * 60 * 60 * 24 * 7));
    const nbrMonths = Math.floor(duration / (1000 * 60 * 60 * 24 * 30));
    const nbrYears = Math.floor(duration / (1000 * 60 * 60 * 24 * 365));

    if (nbrDays === 0) {
      announcement += "moins d'un jour";
    } else if (nbrDays === 1) {
      announcement += `${nbrDays} jour`;
    } else if (nbrDays > 1 && nbrDays < 7) {
      announcement += `${nbrDays} jours`;
    } else if (nbrDays === 7) {
      announcement += '1 semaine';
    } else if (nbrDays > 7 && nbrDays < 30) {
      announcement += `${nbrWeeks} semaines`;
    } else if (nbrDays >= 30 && nbrDays < 365) {
      announcement += `${nbrMonths} mois`;
    } else if (nbrDays > 365 && nbrDays < 365 * 2) {
      announcement += "plus d'1 an";
    } else {
      announcement += `${nbrYears} ans`;
    }

    return announcement;
  }
}
